import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Button from '../../components/UI/Button';
import Input from '../../components/UI/Input';
import Card from '../../components/UI/Card';
import Modal from '../../components/UI/Modal';
import { donationBankAccountsAPI } from '../../services/api';
import { Plus, Edit, Trash2, Save, X, Building2 } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: ${props => props.theme.typography.fontSize['3xl']};
  font-weight: ${props => props.theme.typography.fontWeight.bold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
`;

const BankCard = styled(Card)`
  padding: 1.5rem;
  position: relative;
`;

const BankName = styled.h3`
  font-size: ${props => props.theme.typography.fontSize.lg};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.text.primary};
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BankDetail = styled.p`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.text.secondary};
  margin: 0.15rem 0;
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.25rem 0.75rem;
  border-radius: ${props => props.theme.borderRadius.full};
  font-size: ${props => props.theme.typography.fontSize.xs};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  background-color: ${props => props.$active ? props.theme.colors.success + '20' : props.theme.colors.error + '20'};
  color: ${props => props.$active ? props.theme.colors.success : props.theme.colors.error};
`;

const Actions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const ModalContent = styled.div`
  padding: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${props => props.theme.typography.fontSize.sm};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  color: ${props => props.theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const Textarea = styled.textarea`
  width: 100%;
  min-height: 4.5rem;
  padding: 0.75rem;
  font-size: ${props => props.theme.typography.fontSize.base};
  border: 1px solid ${props => props.theme.colors.border.primary};
  border-radius: ${props => props.theme.borderRadius.md};
  font-family: ${props => props.theme.typography.fontFamily.primary};
  resize: vertical;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.primary}20;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DonationBankAccountsManagement = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [formData, setFormData] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    branch: '',
    swift_code: '',
    currency: 'ETB',
    description: '',
    order: 0,
    is_active: true,
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await donationBankAccountsAPI.getAll({ all: true });
      setAccounts(response.data || []);
    } catch (error) {
      console.error('Error fetching donation bank accounts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        bank_name: account.bank_name || '',
        account_name: account.account_name || '',
        account_number: account.account_number || '',
        branch: account.branch || '',
        swift_code: account.swift_code || '',
        currency: account.currency || 'ETB',
        description: account.description || '',
        order: account.order || 0,
        is_active: account.is_active !== undefined ? account.is_active : true,
      });
    } else {
      setEditingAccount(null);
      setFormData({
        bank_name: '',
        account_name: '',
        account_number: '',
        branch: '',
        swift_code: '',
        currency: 'ETB',
        description: '',
        order: 0,
        is_active: true,
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAccount(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        alert('You are not logged in. Please log in and try again.');
        window.location.href = '/login';
        return;
      }

      if (editingAccount) {
        await donationBankAccountsAPI.update(editingAccount.id, formData);
      } else {
        await donationBankAccountsAPI.create(formData);
      }
      handleCloseModal();
      fetchAccounts();
    } catch (error) {
      console.error('Error saving donation bank account:', error);
      console.error('Error response:', error.response?.data);
      if (error.response?.status === 401) {
        alert('Your session has expired. Please log in again.');
        window.location.href = '/login';
      } else if (error.response?.status === 422) {
        const errors = error.response?.data?.errors;
        if (errors) {
          const errorMessages = Object.entries(errors)
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          alert(`Validation errors:\n${errorMessages}`);
        } else {
          alert('Validation error. Please check all required fields.');
        }
      } else {
        alert('Failed to save donation bank account. Please try again.');
      }
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this bank account?')) return;
    try {
      await donationBankAccountsAPI.delete(id);
      fetchAccounts();
    } catch (error) {
      console.error('Error deleting donation bank account:', error);
      alert('Failed to delete bank account. Please try again.');
    }
  };

  return (
    <Container>
      <Header>
        <Title>Donation Bank Accounts</Title>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <Plus size={20} style={{ marginRight: '0.5rem' }} />
          Add Bank Account
        </Button>
      </Header>

      {loading ? (
        <p>Loading bank accounts...</p>
      ) : accounts.length === 0 ? (
        <Card style={{ padding: '3rem', textAlign: 'center' }}>
          <p>No bank accounts yet. Click "Add Bank Account" to create one.</p>
        </Card>
      ) : (
        <Grid>
          {accounts.map(account => (
            <BankCard key={account.id}>
              <StatusBadge $active={account.is_active}>
                {account.is_active ? 'Active' : 'Inactive'}
              </StatusBadge>
              <BankName>
                <Building2 size={18} />
                {account.bank_name}
              </BankName>
              <BankDetail><strong>Account Name:</strong> {account.account_name}</BankDetail>
              <BankDetail><strong>Account Number:</strong> {account.account_number}</BankDetail>
              {account.branch && (
                <BankDetail><strong>Branch:</strong> {account.branch}</BankDetail>
              )}
              {account.swift_code && (
                <BankDetail><strong>SWIFT:</strong> {account.swift_code}</BankDetail>
              )}
              <BankDetail><strong>Currency:</strong> {account.currency || 'ETB'}</BankDetail>
              {account.description && (
                <BankDetail>{account.description}</BankDetail>
              )}
              <Actions>
                <Button variant="outline" size="small" onClick={() => handleOpenModal(account)}>
                  <Edit size={16} />
                </Button>
                <Button variant="outline" size="small" onClick={() => handleDelete(account.id)}>
                  <Trash2 size={16} />
                </Button>
              </Actions>
            </BankCard>
          ))}
        </Grid>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}
      >
        <ModalContent>
          <form onSubmit={handleSubmit}>
            <FormGroup>
              <Label>Bank Name *</Label>
              <Input
                name="bank_name"
                value={formData.bank_name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Account Name *</Label>
              <Input
                name="account_name"
                value={formData.account_name}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <FormGroup>
              <Label>Account Number *</Label>
              <Input
                name="account_number"
                value={formData.account_number}
                onChange={handleInputChange}
                required
              />
            </FormGroup>

            <Row>
              <FormGroup>
                <Label>Branch</Label>
                <Input
                  name="branch"
                  value={formData.branch}
                  onChange={handleInputChange}
                />
              </FormGroup>
              <FormGroup>
                <Label>SWIFT Code</Label>
                <Input
                  name="swift_code"
                  value={formData.swift_code}
                  onChange={handleInputChange}
                />
              </FormGroup>
            </Row>

            <Row>
              <FormGroup>
                <Label>Currency</Label>
                <Input
                  name="currency"
                  value={formData.currency}
                  onChange={handleInputChange}
                  maxLength={3}
                />
              </FormGroup>
              <FormGroup>
                <Label>Order</Label>
                <Input
                  name="order"
                  type="number"
                  value={formData.order}
                  onChange={handleInputChange}
                  min="0"
                />
              </FormGroup>
            </Row>

            <FormGroup>
              <Label>Description</Label>
              <Textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleInputChange}
                />
                Active (visible on donate page)
              </Label>
            </FormGroup>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <Button type="button" variant="outline" onClick={handleCloseModal}>
                <X size={18} style={{ marginRight: '0.5rem' }} />
                Cancel
              </Button>
              <Button type="submit" variant="primary">
                <Save size={18} style={{ marginRight: '0.5rem' }} />
                {editingAccount ? 'Update' : 'Create'}
              </Button>
            </div>
          </form>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default DonationBankAccountsManagement;


